'use client';

import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/nextjs';
import { get, logger } from '@brahma/utils';
import useGlobalContext from '$hooks/use-global-context';
import { API_URLS } from '$constants/apis';
import { isPwaServiceWorkerEnabled, registerServiceWorker, getServiceWorkerPaths } from './pwa-register.helper';
const logRegistrationError = (message: string, error: unknown, context?: Record<string, string>) => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  logger.error({
    message,
    data: JSON.stringify({
      ...context,
      ...getServiceWorkerPaths(),
      error: errorMessage
    })
  }, true);
  Sentry.captureException(error instanceof Error ? error : new Error(errorMessage), {
    tags: {
      component: 'PwaRegister'
    },
    extra: {
      ...context,
      ...getServiceWorkerPaths()
    }
  });
};

/**
 * Registers the service worker so next-pwa can cache static JS/CSS (StaleWhileRevalidate).
 * next-pwa generates sw.js at build time; we register manually because register: false in next.config
 * (App Router does not auto-register).
 *
 * Uses activeFeatureConfig from global context (SSR). If SSR could not load config, falls back to a
 * client-side /api fetch so unauthenticated routes (e.g. sign-in) still register when the flag is on.
 */
export default function PwaRegister() {
  const {
    state
  } = useGlobalContext();
  const registrationAttemptedRef = useRef(false);
  useEffect(() => {
    if (registrationAttemptedRef.current) {
      return;
    }
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    registrationAttemptedRef.current = true;
    const resolveAndRegister = async () => {
      let enabled = isPwaServiceWorkerEnabled(state.activeFeatureConfig);
      const hasFeatureConfig = state.activeFeatureConfig && Object.keys(state.activeFeatureConfig).length > 0;
      if (!enabled && !hasFeatureConfig) {
        try {
          const clientConfig = await get(API_URLS.getActiveFeatureConfig);
          enabled = isPwaServiceWorkerEnabled(clientConfig);
        } catch (error) {
          logRegistrationError('Failed to fetch active feature config for service worker registration', error);
          return;
        }
      }
      if (!enabled) {
        return;
      }
      try {
        const registration = await registerServiceWorker();
        logger.info({
          message: 'Service worker registered',
          data: JSON.stringify({
            ...getServiceWorkerPaths(),
            scriptURL: registration.active?.scriptURL ?? registration.installing?.scriptURL
          })
        }, true);
      } catch (error) {
        logRegistrationError('Service worker registration failed', error);
      }
    };
    void resolveAndRegister();
  }, [state.activeFeatureConfig]);
  return null;
}